import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** @type {MutationObserver | null} */
  #slideAriaObserver = null;

  /** Pending deferred re-announce (see #announceResults). */
  #announceTimer = null;

  /**
   * Per-instance prefix for generated option ids. aria-activedescendant is
   * resolved document-wide, so two components on one page must not mint the
   * same id.
   */
  #optionIdPrefix = `vm-ps-opt-${Math.random().toString(36).slice(2, 8)}-`;

  /**
   * Monotonic, NOT the ring index. Revealing the "+N more" pills grows the ring
   * and shifts every index after them, so index-derived ids collide with ones
   * already handed out on an earlier pass (proved in-browser: 28 options, 6
   * duplicate ids). aria-activedescendant then resolves to the wrong row.
   */
  #optionSeq = 0;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    // #controller is a field initialised once at construction, and
    // disconnectedCallback aborts it. A Theme-Editor section re-render moves
    // nodes, so connectedCallback can run again against an already-aborted
    // signal -- every listener below would then silently fail to register and
    // the dropdown would lose its dismissal. Start each connection fresh.
    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      // No dialog ancestor means no dismissal at all: every close path above is
      // dialog-gated, so a header instance would leave the results panel open
      // over the page until Escape. Collapse on an outside pointer or on focus
      // leaving the component -- but do NOT call resetSearch(), which clears the
      // input. Someone clicking away has not asked to lose what they typed.
      document.addEventListener('pointerdown', this.#collapseOnOutside, { signal, capture: true });
      this.addEventListener('focusout', this.#collapseOnFocusOut, { signal });
      this.addEventListener('focusin', this.#expandIfPopulated, { signal });
    }

    // The empty-state / recently-viewed panel has no [data-search-results], so
    // a header instance can never reveal it. Fetching it on idle -- two requests
    // for every returning shopper on every page -- would be pure waste there.
    if (dialog && RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Show the first few suggested-search pills plus a "+N more" pill that reveals
   * the rest on click.
   *
   * Search & Discovery returns up to 10 suggestions. Wrapping all of them takes
   * ~192px of a 520px panel and pushes products down to four in the fold;
   * showing four keeps six products visible. Rather than silently dropping the
   * remainder -- which is what a CSS cap did -- the extras are one click away
   * and the control says how many there are.
   *
   * Runs after every morph because the panel is re-rendered on each keystroke,
   * so the expanded state correctly resets with each new query. Header only:
   * the modal is wide enough not to need it.
   */
  /**
   * Move the result count into the persistent live region.
   *
   * The section renders its own role="status" region, but morph injects it with
   * the text already inside. A live region has to be in the accessibility tree
   * BEFORE its content changes, so an already-populated one announces nothing:
   * the first search of a visit was silent, and only later searches spoke,
   * because morph reused the node by then.
   *
   * The header's own region (ref="liveRegion") sits outside the morph target and
   * is present from first paint. Copy the text there, and strip the injected
   * region's live semantics so the two cannot both announce.
   */
  /**
   * Give every row in the keyboard ring real option semantics.
   *
   * Done here rather than in Liquid on purpose: #allResultsItems is what
   * ArrowUp/ArrowDown actually walk, and it is computed at runtime (it spans
   * three different markup shapes -- query <li>s, product <li>s and slideshow
   * slides -- and drops rows hidden behind "+N more"). Emitting role="option"
   * from the templates would let the announced options drift out of step with
   * the rows that can actually be selected. One source of truth.
   *
   * Trade-off worth naming: an option owning a link is a spec nit (an option
   * should not have interactive descendants). Keeping the anchors tabbable is
   * deliberate -- Tab into the results is how a keyboard user reaches them
   * today, and stripping it to satisfy the letter of the pattern would undo the
   * WCAG 2.1.2 fix that removed the Tab trap. Both routes now work.
   */
  #syncOptionRoles() {
    const inRing = new Set(this.#allResultsItems);
    if (!inRing.size) return;

    // Every row that COULD be in the ring, hidden ones included -- the sweep has
    // to be able to take option semantics back off a row that left it.
    const candidates = this.refs.predictiveSearchResults.querySelectorAll(
      '.predictive-search-results__card, .predictive-search-results__card--query'
    );

    for (const row of candidates) {
      if (inRing.has(row)) {
        row.setAttribute('role', 'option');
        if (!row.id) row.id = `${this.#optionIdPrefix}${this.#optionSeq++}`;
        // Explicit false, not absent: this is a single-select listbox, and the
        // CSS highlight keys off [aria-selected='true'] so 'false' is inert.
        if (row.getAttribute('aria-selected') !== 'true') {
          row.setAttribute('aria-selected', 'false');
        }
        this.#nameOption(row);
      } else if (row.getAttribute('role') === 'option') {
        // Collapsing "+N more" pushes rows back out of the ring. Leaving them as
        // options announces a count the keyboard cannot walk -- 28 options for
        // 22 reachable rows, which is the same WCAG 2.4.7 defect the ring filter
        // exists to prevent, one layer up.
        //
        // And if the combobox was pointing at THIS row (arrow into a revealed
        // pill, then "Show less"), it must stop: a combobox naming a hidden,
        // role-less element is read aloud by JAWS on the next focus.
        if (this.refs.searchInput?.getAttribute('aria-activedescendant') === row.id) {
          this.#setActiveDescendant(null);
        }
        row.removeAttribute('role');
        row.removeAttribute('aria-selected');
      }
    }
  }

  /**
   * Give a card-backed option a clean accessible name.
   *
   * role="option" names itself from content, and a resource card carries its
   * title THREE times (a visually-hidden span inside the link, the image alt,
   * the visible title) -- so each ArrowDown read a 140-character product title
   * two or three times before reaching the price. Point aria-labelledby at the
   * visible title plus the one element after it in the card body (the price
   * block for products, the "N products" count for collections), which is
   * exactly what a sighted shopper reads. Query pills have no card and keep
   * their natural name.
   *
   * Ids are minted off the row id, which morph() wipes on reuse, so a reused
   * node is re-named for the row it now holds.
   */
  #nameOption(row) {
    const title = row.querySelector('.resource-card__title');
    if (!title) return;
    if (!title.id) title.id = `${row.id}-t`;
    const extra = row.querySelector('.resource-card__content > :not(.resource-card__title)');
    if (extra && !extra.id) extra.id = `${row.id}-x`;
    row.setAttribute('aria-labelledby', extra ? `${title.id} ${extra.id}` : title.id);
  }

  /** Point the combobox at the active row, or at nothing. */
  #setActiveDescendant(item) {
    const input = this.refs.searchInput;
    if (!input) return;
    if (item?.id) input.setAttribute('aria-activedescendant', item.id);
    else input.removeAttribute('aria-activedescendant');
  }

  /**
   * aria-expanded reflects whether the popup is actually on screen: not
   * collapsed AND populated. Derived rather than passed in -- an optimistic
   * "true" on the keystroke (the fetch has not resolved; CSS still hides the
   * panel) or a stale "true" after a fetch that failed or returned nothing both
   * describe a popup the shopper cannot see.
   */
  #setExpanded() {
    const input = this.refs.searchInput;
    if (!input) return;
    const showing =
      !('collapsed' in this.dataset) &&
      !!this.refs.predictiveSearchResults?.querySelector('[data-search-results]');
    input.setAttribute('aria-expanded', String(showing));
  }

  #announceResults() {
    const region = this.refs.liveRegion;
    if (!region) return;

    const injected = this.refs.predictiveSearchResults.querySelector('[role="status"]');
    const message = injected ? injected.textContent.trim() : '';

    if (injected) {
      // role="presentation", not removed: this node is a direct child of
      // #predictive-search-results, which is a listbox, and a listbox may only
      // own options and groups. Stripping the role outright left a generic div
      // owned by the listbox (found in the browser -- the popup's first child
      // was "<div NO ROLE>"). Presentation removes it from the tree entirely,
      // which is what a defused duplicate should be.
      injected.setAttribute('role', 'presentation');
      injected.removeAttribute('aria-live');
    }

    // A live region only fires on a content CHANGE, and the accessibility layer
    // coalesces every DOM edit made in one task — so clearing and re-setting
    // synchronously is a no-op and two searches with the same count stay silent.
    // Land the empty state now and the message on a later task, so the AX tree
    // sees two distinct changes.
    clearTimeout(this.#announceTimer);
    this.#announceTimer = null;

    if (message && region.textContent === message) {
      region.textContent = '';
      this.#announceTimer = setTimeout(() => {
        this.#announceTimer = null;
        region.textContent = message;
      }, 100);
      return;
    }

    region.textContent = message;
  }

  /**
   * Neutralise the carousel machinery on the flattened collection list.
   *
   * The header CSS lays every slide out at once, but slideshow-slide.liquid
   * server-renders aria-hidden="true" on all but the first, and only
   * slideshow.js's IntersectionObserver ever flips it -- machinery the
   * flattening made meaningless. Nine of ten visible rows shipped
   * aria-hidden="true" with a tabbable link inside: axe `aria-hidden-focus`,
   * WCAG 4.1.2.
   *
   * The same component also binds a drag handler that preventDefault()s every
   * mousedown and cancels the next click after 1px of horizontal movement, so a
   * press-and-drift on a collection link simply did not navigate. Setting
   * `disabled` makes slideshow.js skip both, which is exactly what a flattened
   * list wants.
   */
  #tameFlattenedCarousels() {
    if (this.dialog) return;
    const root = this.refs.predictiveSearchResults;
    for (const show of root.querySelectorAll('slideshow-component')) {
      show.setAttribute('disabled', 'true');
    }
    for (const slide of root.querySelectorAll('slideshow-slide')) {
      slide.setAttribute('aria-hidden', 'false');
    }

    // A one-shot write is not enough. slideshow.js's #updateVisibleSlides()
    // is gated by NOTHING -- not the `disabled` attribute, not its private
    // field -- and unconditionally re-writes aria-hidden for every slide from
    // an IntersectionObserver callback, a transition end, and a rAF. Worse,
    // morph() reuses the existing <slideshow-component>, so connectedCallback
    // does not re-run and slides added by a later query are never observed:
    // they get aria-hidden="true" and keep it, with tabbable links inside.
    // Hold the value instead of setting it once.
    if (!this.#slideAriaObserver) {
      this.#slideAriaObserver = new MutationObserver((records) => {
        for (const record of records) {
          const el = record.target;
          if (
            el instanceof HTMLElement &&
            el.tagName.toLowerCase() === 'slideshow-slide' &&
            el.getAttribute('aria-hidden') !== 'false'
          ) {
            el.setAttribute('aria-hidden', 'false');
          }
        }
      });
    }
    this.#slideAriaObserver.disconnect();
    this.#slideAriaObserver.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }

  #collapseExtraQueries() {
    if (this.dialog) return;

    const list = this.refs.predictiveSearchResults.querySelector(
      '.predictive-search-results__wrapper-queries'
    );
    if (!list) return;

    const VISIBLE = 4;
    const pills = Array.from(list.children);
    // Only worth a control if it hides more than the control itself occupies.
    if (pills.length <= VISIBLE + 1) return;

    const extras = pills.slice(VISIBLE);

    const item = document.createElement('li');
    item.className = 'predictive-search-results__card--query vm-more-queries';
    // The control is a button, not a result, so it must not be an option: it is
    // excluded from the keyboard ring and role="presentation" keeps it from
    // being counted as one of the group's children. (The follow-up this comment
    // used to name is done -- the pills are real options now.)
    item.setAttribute('role', 'presentation');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pills__pill predictive-search-results__pill vm-more-queries__button';

    let expanded = false;

    const apply = (next) => {
      expanded = next;
      for (const pill of extras) pill.hidden = !expanded;
      // Revealing/hiding extras changes the keyboard ring, so the option roles
      // and ids have to be re-stamped or the newly shown pills are unreachable
      // to aria-activedescendant.
      this.#syncOptionRoles();
      button.textContent = expanded ? 'Show less' : `+${extras.length} more`;
      button.setAttribute('aria-expanded', String(expanded));
      button.setAttribute(
        'aria-label',
        expanded
          ? `Show fewer suggested searches, hiding ${extras.length}`
          : `Show ${extras.length} more suggested searches`
      );
      if (expanded) item.dataset.expanded = '';
      else delete item.dataset.expanded;
    };

    button.addEventListener('click', () => {
      apply(!expanded);
      if (expanded) {
        // Focus the first pill just revealed, not the input: the control the
        // user activated is still here, and sending focus backwards would make
        // them Tab past four pills to reach the ones they asked for.
        extras[0]?.querySelector('a')?.focus();
      } else {
        // Focus may be sitting on a pill that is now hidden, which would drop it
        // to <body>. Bring it back to the control that did the collapsing.
        button.focus();
      }
    });

    apply(false);
    item.appendChild(button);
    list.appendChild(item);
  }

  /**
   * Hide the results panel without touching the query. Visibility is CSS-driven
   * off this attribute, so nothing is refetched and nothing is cleared.
   */
  collapse = () => {
    // Clear the selection BEFORE setting the flag (the getter above returns []
    // once it is set). Done by hand rather than through `#currentIndex = -1`,
    // whose setter ends with searchInput.focus() -- that would yank focus back
    // from whatever the shopper just clicked.
    for (const item of this.#allResultsItems) {
      item.setAttribute('aria-selected', 'false');
      item.classList.remove('keyboard-focus');
    }
    // A closed popup must not leave the combobox pointing at a row nobody can
    // see, and must not advertise itself as expanded. #setExpanded derives from
    // the flag, so it runs AFTER the flag is set.
    this.#setActiveDescendant(null);
    this.dataset.collapsed = '';
    this.#setExpanded();
  };

  /** Re-show a panel that is still populated. */
  #expand = () => {
    delete this.dataset.collapsed;
    // Re-stamp on every open. collapse() does not abort the in-flight fetch,
    // so a response can land while the panel is shut -- and #allResultsItems
    // is [] while collapsed, so the post-render sweep finds nothing to stamp.
    // Without this, click-away-during-fetch then click-back restores the
    // exact original defect (arrowing over role-less rows) AND announces
    // "combobox, expanded" over a listbox with zero options. Idempotent.
    this.#syncOptionRoles();
    this.#setExpanded();
  };

  /**
   * Focus returning to a collapsed-but-populated box should reopen it -- a
   * persistent search box that stays shut until you type another character
   * reads as broken. Safe because collapse() has already dropped the selection.
   */
  #expandIfPopulated = () => {
    if (this.refs.predictiveSearchResults.querySelector('[data-search-results]')) {
      this.#expand();
    }
  };

  /** @param {Event} event */
  #collapseOnOutside = (event) => {
    const target = /** @type {Node | null} */ (event.target);
    if (target && this.contains(target)) return;
    this.collapse();
  };

  /** @param {FocusEvent} event */
  #collapseOnFocusOut = (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    // relatedTarget is null when focus leaves the document entirely (tab away
    // from the browser); keep the panel as-is in that case.
    if (next === null || this.contains(next)) return;
    this.collapse();
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#slideAriaObserver?.disconnect();
    this.#slideAriaObserver = null;
    clearTimeout(this.#announceTimer);
    this.#announceTimer = null;
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    // This prefetch runs on idle for anyone with recently-viewed products, and
    // resetSearch() clears the input. The modal's input had no value attribute
    // so that was invisible; the header's is prefilled with search.terms on
    // /search, and blanking it takes the shopper's own query off the screen a
    // moment after the page settles.
    if (this.refs.searchInput.value.length > 0) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    // A collapsed panel is hidden but still populated, and every key path keys
    // off this list. Without this guard: type, ArrowDown, click away (collapse
    // leaves the input filled and the DOM intact), click back, press Enter --
    // and #currentIndex still reads >= 0, so Enter navigated to a product the
    // shopper could not see instead of running the search. Returning [] makes
    // the `!length` guard in onSearchKeyDown swallow every key while collapsed,
    // so Enter falls through to the form's own submit, which is what is wanted.
    if ('collapsed' in this.dataset) return [];

    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      // Hidden extras (behind "+N more") must leave the ring: otherwise six
      // ArrowDown presses select elements that are display:none, the highlight
      // vanishes, and Enter navigates to a suggestion never shown. WCAG 2.4.7.
      .filter((item) => item instanceof HTMLElement && !item.hidden);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.setAttribute('aria-selected', 'false');
      }
    }

    // Focus stays in the input while arrowing, so this attribute is the ONLY
    // thing that tells a screen reader which row is active.
    this.#setActiveDescendant(activeItem);

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    // Query pills in sections/predictive-search.liquid carry their own
    // on:keydown="/onSearchKeyDown", so once native Tab can reach a pill link,
    // Enter on it bubbles here too. With nothing arrowed, #currentIndex is -1
    // and the branch below would preventDefault() the link and submit the FORM
    // instead. Enter belongs to whatever is focused unless that is the input.
    if (event.key === 'Enter' && document.activeElement !== this.refs.searchInput) return;

    if (event.key === 'Escape') {
      // Do NOT collapse() here first. #resetSearch sets #currentIndex = -1, and
      // that setter is what returns focus to the input -- but it early-returns
      // when the panel is collapsed (no items). Collapsing first meant Escape
      // pressed on a suggested-query pill cleared everything, removed the
      // focused link, and dropped keyboard focus to <body>. The dialog-less
      // branch of #resetSearch collapses on its own, after the refocus.
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      // Tab is deliberately NOT handled. It used to preventDefault() in both
      // directions and cycle the selection, and the #currentIndex setter ends
      // by refocusing the input -- so once results were showing, focus could
      // not leave the field by keyboard at all. Inside a modal dialog that read
      // as a focus trap; bound to a persistent header input it is a WCAG 2.1.2
      // No Keyboard Trap failure on every page of the store. The results are
      // real links, so native Tab now walks into them, and ArrowUp/ArrowDown
      // still drive the selection.

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          // The panel is whatever the LAST completed fetch rendered. With
          // variants.sku searchable, single-result panels are common: type a
          // full SKU, get one product, append a character, press Enter inside
          // the debounce+fetch window -- and this shortcut would navigate to the
          // stale product instead of submitting the edited query. Honour it only
          // when the panel's own _psq matches what is in the box right now.
          const href = singleResultContainer.dataset.singleResultUrl;
          let fresh = false;
          try {
            const psq = new URL(href, location.origin).searchParams.get('_psq');
            fresh = psq !== null && psq.trim() === this.refs.searchInput.value.trim();
          } catch {
            fresh = false;
          }
          if (fresh) {
            event.preventDefault();
            window.location.href = href;
            return;
          }
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          // Submit the owning form rather than hand-building a URL. Hand-building
          // dropped every other field on the form -- the header's type=product and
          // the modal's options[prefix]=last -- and skipped preventDefault, so the
          // native Enter submit raced this assignment to a DIFFERENT result set.
          // requestSubmit() keeps it to exactly one navigation, with every field.
          const form = this.refs.searchInput.form;

          if (form && typeof form.requestSubmit === 'function') {
            event.preventDefault();
            form.requestSubmit();
          } else {
            const searchUrl = new URL(Theme.routes.search_url, location.origin);
            searchUrl.searchParams.set('q', this.refs.searchInput.value);
            window.location.href = searchUrl.toString();
          }
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#expand();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // NO resources[options][fields] here, and no resources[type]: Search &
    // Discovery owns both. An earlier version pinned the field list to add
    // variants.sku, justified by a measurement that does not reproduce -- SKU
    // 212-50 is absent from its product's title and still matches with
    // fields=title and with no param at all, because S&D overrides the Liquid
    // option. The line was inert, and hardcoding a field list would have seized
    // control of searchable fields from the merchant the moment S&D changed.

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);
        this.#collapseExtraQueries();
        this.#tameFlattenedCarousels();
        // AFTER the two above: #collapseExtraQueries hides the overflow pills
        // and #allResultsItems drops hidden rows, so syncing earlier would
        // stamp role="option" on rows that are not selectable.
        this.#syncOptionRoles();
        this.#setExpanded();
        this.#announceResults();

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    // No dialog means no empty state to show: clear, collapse, drop the results
    // and stop. The two section fetches below exist for the modal's
    // recently-viewed panel, which the header never displays.
    if (!this.dialog) {
      // Abort first. Without this an in-flight /search/suggest survives the
      // reset: its .then sees aborted === false, morphs the panel back in and
      // announces. Sighted users never see it (the wrapper is display:none
      // while collapsed), so before the live region existed this was an
      // invisible glitch -- but it now SPEAKS a count for a query the shopper
      // just cleared, on Escape/Clear/delete-to-empty, which are exactly the
      // gestures screen-reader users reach for.
      this.#activeFetch?.abort();
      this.#activeFetch = null;

      this.collapse();
      predictiveSearchResults.replaceChildren();
      clearTimeout(this.#announceTimer);
      this.#announceTimer = null;
      if (this.refs.liveRegion) this.refs.liveRegion.textContent = '';
      this.#setActiveDescendant(null);
      this.#setExpanded();
      return;
    }

    // NOTE: this dialog branch deliberately does NOT clear liveRegion or cancel
    // #announceTimer -- the modal instance has no ref="liveRegion", so there is
    // nothing to clear. If you ever add one to snippets/search-modal.liquid,
    // mirror the two lines from the dialog-less branch above or closing the
    // modal will leave a stale count readable and a timer armed.
    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    // Mirror the dialog-less branch: closing the modal must not leave its
    // combobox pointing at a row from the old results, or reporting expanded.
    this.#setActiveDescendant(null);
    this.#setExpanded();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
